/* Complete your profile — the desktop's OnboardingModal / AdminOnboardingModal: Contact → School → Review.
 * The School step is skipped when the license already names the school (and for master admins). */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getSession, setSession } from '../api/client.js';
import { useSession } from '../app/session.jsx';
import { Button, Card, Icon, Input, Select, Skeleton } from '../ui/index.jsx';
import { INTL, US_STATES, isIntl } from '../features/auth/regions.js';
import '../app/shell.css';
import '../features/auth/auth.css';
import { Trans, useTranslation } from 'react-i18next';

const ROLES = ['Teacher', 'Media Specialist', 'Librarian', 'Principal', 'Assistant Principal', 'Administrator'];
const digits = (s) => s.replace(/\D/g, '');

function Searchable({ id, label, value, onChange, options, disabled, placeholder }) {
  const { t } = useTranslation('bv');
  const [q, setQ] = useState('');
  const shown = useMemo(() => options.filter((o) => o.label.toLowerCase().includes(q.trim().toLowerCase())).slice(0, 400), [options, q]);
  return (
    <div className="bv-pick">
      <label className="bv-label" htmlFor={id}>{label}</label>
      <Input id={`${id}-q`} aria-label={t('ob.search', { label })} placeholder={disabled ? placeholder : t('ob.typeToSearch')} value={q} onChange={(e) => setQ(e.target.value)} disabled={disabled} />
      <Select id={id} value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        options={[{ value: '', label: disabled ? placeholder : t('ob.selectX', { label: label.toLowerCase() }) }, ...shown]} />
    </div>
  );
}

export default function OnboardingPage() {
  const { t } = useTranslation('bv');
  const { signOut } = useSession();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(0);
  const [c, setC] = useState({ first_name: '', last_name: '', phone_number: '', phone_extension: '', user_role: '' });
  const [state, setState] = useState('');
  const [district, setDistrict] = useState('');
  const [school, setSchool] = useState('');
  const [districts, setDistricts] = useState([]);
  const [schools, setSchools] = useState([]);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { api('/auth/onboarding-status').then((r) => { if (r.ok) { if (r.data.completed) navigate('/', { replace: true }); else setStatus(r.data); } else setErr(r.error); }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const skipSchool = !!(status && (status.masterAdmin || status.licenseSchool));
  const steps = skipSchool ? ['Contact', 'Review'] : ['Contact', 'School', 'Review'];
  const intl = isIntl(state);
  useEffect(() => {
    setDistrict(''); setSchool(''); setDistricts([]); setSchools([]);
    if (!state || intl) return;
    api(`/auth/schools/districts?state=${encodeURIComponent(state)}`, { auth: false }).then((r) => (r.ok ? setDistricts(r.data.data) : setErr(t('ob.districtsFailed'))));
  }, [state]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSchool(''); setSchools([]);
    if (!district) return;
    api(`/auth/schools/list?district_id=${encodeURIComponent(district)}&state=${encodeURIComponent(state)}`, { auth: false }).then((r) => (r.ok ? setSchools(r.data.data) : setErr(t('ob.schoolsFailed'))));
  }, [district]); // eslint-disable-line react-hooks/exhaustive-deps

  const contactOk = c.first_name.trim() && c.last_name.trim() && [10, 11].includes(digits(c.phone_number).length) && c.user_role;
  const schoolOk = state && (intl || school);
  const next = () => {
    setErr(null);
    if (steps[step] === 'Contact' && !contactOk) { setErr(digits(c.phone_number).length && ![10, 11].includes(digits(c.phone_number).length) ? t('ob.phone10') : t('ob.allFields')); return; }
    if (steps[step] === 'School' && !schoolOk) { setErr(t('ob.pickSchool')); return; }
    setStep((s) => s + 1);
  };
  const submit = async () => {
    if (!terms) { setErr(t('ob.acceptTerms')); return; }
    setBusy(true); setErr(null);
    const body = { ...c, user_role: status.masterAdmin ? 'Master Admin' : c.user_role, terms_accepted: true, ...(skipSchool ? { use_license_school: !status.masterAdmin } : { school_state: state, school_district: district, school_name: school }) };
    const r = await api('/auth/onboarding', { method: 'POST', body });
    setBusy(false);
    if (!r.ok) { setErr((r.data && r.data.errors && r.data.errors.join(' · ')) || r.error); return; }
    const s = getSession(); if (s) setSession({ ...s, onboardingCompleted: true });
    navigate('/', { replace: true });
  };
  const schoolName = status && status.licenseSchool ? status.licenseSchool.school_name : (schools.find((x) => x.id === school) || {}).school_name;
  return (
    <div className="bv-signin">
      <Card className="bv-signin__card is-wide" pad={false}>
        <img className="bv-signin__logo" src="/brand/logo-long-on-light.svg" alt="Brightivity" />
        <h1 className="bv-signin__title">{t('ob.title')}</h1>
        <p className="bv-signin__sub">{t('ob.sub')}</p>
        {!status ? <Skeleton height={200} radius={18} /> : (
          <div data-testid="onboarding">
            <ol className="bv-steps">{steps.map((s, i) => <li key={s} className={i === step ? 'is-on' : i < step ? 'is-done' : ''}><span>{i < step ? <Icon name="check" size={14} /> : i + 1}</span>{t(`ob.step.${s.toLowerCase()}`)}</li>)}</ol>
            {steps[step] === 'Contact' && (
              <div className="bv-signup">
                <div className="bv-signup__row">
                  <div><label className="bv-label" htmlFor="ob-first">{t('ob.firstName')}</label><Input id="ob-first" value={c.first_name} onChange={(e) => setC({ ...c, first_name: e.target.value })} autoFocus /></div>
                  <div><label className="bv-label" htmlFor="ob-last">{t('ob.lastName')}</label><Input id="ob-last" value={c.last_name} onChange={(e) => setC({ ...c, last_name: e.target.value })} /></div>
                </div>
                <div className="bv-signup__row">
                  <div style={{ flex: 3 }}><label className="bv-label" htmlFor="ob-phone">{t('ob.phone')}</label><Input id="ob-phone" inputMode="tel" placeholder="(555) 123-4567" value={c.phone_number} onChange={(e) => setC({ ...c, phone_number: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label className="bv-label" htmlFor="ob-ext">{t('ob.ext')}</label><Input id="ob-ext" inputMode="numeric" placeholder="123" value={c.phone_extension} onChange={(e) => setC({ ...c, phone_extension: digits(e.target.value).slice(0, 10) })} /></div>
                </div>
                {!status.masterAdmin && (<><label className="bv-label" htmlFor="ob-role">{t('ob.role')}</label>
                  <Select id="ob-role" value={c.user_role} onChange={(e) => setC({ ...c, user_role: e.target.value })} options={[{ value: '', label: t('ob.selectRole') }, ...ROLES.map((r) => ({ value: r, label: t(`ob.roles.${r.replace(/ /g, '')}`) }))]} /></>)}
              </div>
            )}
            {steps[step] === 'School' && (
              <div className="bv-signup">
                <Searchable id="ob-state" label={t('ob.stateOrCountry')} value={state} onChange={setState}
                  options={[...US_STATES.map((n) => ({ value: n, label: n })), ...Object.keys(INTL).sort().map((n) => ({ value: n, label: t('ob.countrySuffix', { name: n }) }))]} />
                {!intl && <Searchable id="ob-district" label={t('ob.district')} value={district} onChange={setDistrict} disabled={!state} placeholder={t('ob.pickStateFirst')} options={districts.map((d) => ({ value: d.id, label: d.district_name }))} />}
                {!intl && <Searchable id="ob-school" label={t('ob.step.school')} value={school} onChange={setSchool} disabled={!district} placeholder={t('ob.pickDistrictFirst')} options={schools.map((s) => ({ value: s.id, label: s.city ? `${s.school_name} — ${s.city}` : s.school_name }))} />}
                <p className="bv-small bv-warntext">{t('ob.locked')}</p>
              </div>
            )}
            {steps[step] === 'Review' && (
              <div className="bv-review">
                <dl>
                  <dt>{t('common.name')}</dt><dd>{c.first_name} {c.last_name}</dd>
                  <dt>{t('ob.phoneShort')}</dt><dd>{c.phone_number}{c.phone_extension ? ` ${t('ob.extLower')} ${c.phone_extension}` : ''}</dd>
                  <dt>{t('ob.roleShort')}</dt><dd>{status.masterAdmin ? t('ob.masterAdmin') : (ROLES.includes(c.user_role) ? t(`ob.roles.${c.user_role.replace(/ /g, '')}`) : c.user_role)}</dd>
                  {!status.masterAdmin && <><dt>{t('ob.step.school')}</dt><dd>{schoolName || state}</dd></>}
                </dl>
                <label className="bv-check"><input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} data-testid="ob-terms" />
                  <span><Trans t={t} i18nKey="ob.terms" components={{ terms: <a href="https://www.posterstudio.com/terms" target="_blank" rel="noreferrer" />, privacy: <a href="https://www.posterstudio.com/privacy" target="_blank" rel="noreferrer" /> }} /></span></label>
              </div>
            )}
            {err && <p className="bv-signin__msg bv-signin__msg--err" role="alert">{err}</p>}
            <div className="bv-modal-actions">
              {step > 0 ? <Button onClick={() => { setErr(null); setStep((s) => s - 1); }}>{t('common.back')}</Button> : <Button onClick={() => { signOut(); navigate('/sign-in'); }}>{t('shell.signOut')}</Button>}
              {step < steps.length - 1 ? <Button variant="coral" onClick={next} data-testid="ob-next">{t('common.next')}</Button>
                : <Button variant="coral" disabled={busy} onClick={submit} data-testid="ob-submit">{busy ? t('common.saving') : t('ob.finish')}</Button>}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
